import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Container } from '../../../components/web/container/container';
import { useRootStore } from '../../../providers/root-store-provider';
import { CompoundInterestParams } from '../../../utils/compound-interest/types';
import { DEFAULT_PARAMS } from '../../../utils/compound-interest/constants';
import { calculateCompoundInterest, calculateSummary } from '../../../utils/compound-interest/calculations';
import { CompoundInterestCalculator } from './calculator/compound-interest-calculator';
import { CompoundInterestSummary } from './summary/compound-interest-summary';
import { CompoundInterestGrowthChart } from './charts/growth-chart/compound-interest-growth-chart';
import { CompoundInterestTable } from './table/compound-interest-table';
import { Cta } from '../cta/cta';
import { useCompoundInterestPrefill } from './hooks/use-compound-interest-prefill';

export const CompoundInterest: React.FC = observer(() => {
    const { t } = useTranslation();
    const { authStore, portfolioStore, cookieService } = useRootStore();

    const isLoggedIn = cookieService.hasJwtToken() && !!authStore.userProfile;
    const { hasPortfolio } = portfolioStore;
    const hasAttemptedPortfolioFetch = useRef(false);

    useEffect(() => {
        if (!isLoggedIn) {
            hasAttemptedPortfolioFetch.current = false;
            return;
        }

        const shouldFetchPortfolioList =
            portfolioStore.portfolios.length === 0 && !portfolioStore.loading && !hasAttemptedPortfolioFetch.current;

        if (!shouldFetchPortfolioList) {
            return;
        }

        hasAttemptedPortfolioFetch.current = true;
        portfolioStore.fetchPortfolioList();
    }, [isLoggedIn, portfolioStore]);

    const selectedPortfolio = isLoggedIn && hasPortfolio ? portfolioStore.selectedPortfolio : null;

    const [params, setParams] = useState<CompoundInterestParams>(DEFAULT_PARAMS);
    const resultsChartSectionRef = useRef<HTMLDivElement | null>(null);
    const handlePrefill = useCallback((nextParams: CompoundInterestParams) => {
        setParams(nextParams);
    }, []);

    const {
        prefilledPortfolioName,
        hasPrefilledFromPortfolio,
        canResetToPrefilledPortfolioParams,
        markParamsAsEdited,
        resetToPrefilledPortfolioParams,
    } = useCompoundInterestPrefill({
        params,
        isLoggedIn,
        hasPortfolio,
        portfolio: selectedPortfolio
            ? {
                  id: selectedPortfolio.id,
                  name: selectedPortfolio.name,
                  value: selectedPortfolio.portfolioValue,
                  currency: selectedPortfolio.portfolioHistoryCurrencyCode,
              }
            : null,
        onPrefill: handlePrefill,
    });

    const handleParamsChange = (nextParams: CompoundInterestParams) => {
        markParamsAsEdited();
        setParams(nextParams);
    };

    const handleScrollToResults = () => {
        resultsChartSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const results = useMemo(() => calculateCompoundInterest(params), [params]);

    const summary = useMemo(() => calculateSummary(results, params), [results, params]);

    return (
        <div className="relative pb-[8rem] pt-[4rem] text-web-copy vl:pt-[6rem] vl:pb-[12rem]">
            <Container>
                <CompoundInterestCalculator
                    params={params}
                    onChange={handleParamsChange}
                    isLoggedIn={isLoggedIn}
                    prefilledPortfolioName={prefilledPortfolioName}
                    hasPrefilledFromPortfolio={hasPrefilledFromPortfolio}
                    canResetPrefill={canResetToPrefilledPortfolioParams}
                    onResetPrefill={resetToPrefilledPortfolioParams}
                    onShowResultsClick={handleScrollToResults}
                />

                <div
                    className="mt-[7rem] scroll-mt-[10.4rem] md:mt-[10rem]"
                    ref={resultsChartSectionRef}
                    id="compound-interest-results"
                >
                    <CompoundInterestGrowthChart results={results} params={params} />
                </div>

                <div className="mt-[2.4rem] md:mt-[3.2rem]">
                    <CompoundInterestSummary summary={summary} params={params} />
                </div>

                <div className="mt-[7rem] md:mt-[10rem]">
                    <CompoundInterestTable results={results} params={params} />
                </div>
            </Container>
            {!isLoggedIn && (
                <Cta
                    compact
                    title={t('pages.compoundInterest.cta.title')}
                    titleGrey={t('pages.compoundInterest.cta.subtitle')}
                    buttonText={t('pages.compoundInterest.cta.button')}
                    signup
                />
            )}
        </div>
    );
});
